import { AfterViewInit, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { IModalSettings, ModalComponent } from '../../../shared/components/modal/modal.component';
import { IModalComponent } from '../../../shared/components/modal/services/modal.service';
import {
  IConversationFile,
  MAX_ATTACHMENTS_PER_MESSAGE
} from '../../interfaces/conversation-file.interface';
import { normalizeText } from '../../utils/normalize-text.util';

@Component({
  selector: 'sgt-file-picker-modal',
  templateUrl: './file-picker-modal.component.html',
  styleUrls: ['./file-picker-modal.component.scss']
})
export class FilePickerModalComponent implements AfterViewInit, IModalComponent {
  readonly settings: IModalSettings = {
    width: '560px',
    customClass: 'modal--file-picker',
    allowClickOutsideToClose: true,
    position: 'center',
    hasHeader: false,
    hasFooter: false
  };

  readonly maxSelection = MAX_ATTACHMENTS_PER_MESSAGE;

  files: IConversationFile[] = [];
  selection: string[] = [];
  query = '';

  @ViewChild(ModalComponent) modalRef: ModalComponent;
  @Output() filesPicked = new EventEmitter<IConversationFile[]>();
  @Output() closed = new EventEmitter<boolean>();

  get visibleFiles(): IConversationFile[] {
    const termo = normalizeText(this.query.trim());
    return termo
      ? this.files.filter((file) => normalizeText(file.name).includes(termo))
      : this.files;
  }

  get isEmpty(): boolean {
    return this.visibleFiles.length === 0;
  }

  get isFull(): boolean {
    return this.selection.length >= this.maxSelection;
  }

  get countLabel(): string {
    const total = this.selection.length;
    if (!total) {
      return `Escolha até ${this.maxSelection} arquivos`;
    }
    return total === 1
      ? `1 de ${this.maxSelection} arquivos`
      : `${total} de ${this.maxSelection} arquivos`;
  }

  ngAfterViewInit(): void {
    if (!this.modalRef) {
      return;
    }
    setTimeout(() => this.modalRef.open(), 0);
    this.modalRef.closed.subscribe((result: boolean) => this.closed.emit(result));
  }

  /** Só o arquivo registrado como anexo tem identificador aceito no envio. */
  isReusable(file: IConversationFile): boolean {
    return !!file.attachmentId;
  }

  isSelected(file: IConversationFile): boolean {
    return this.selection.includes(file.fileId);
  }

  isBlocked(file: IConversationFile): boolean {
    return !this.isReusable(file) || (this.isFull && !this.isSelected(file));
  }

  hintFor(file: IConversationFile): string {
    if (!this.isReusable(file)) {
      return 'Este arquivo não pode ser reenviado';
    }
    return this.isBlocked(file) ? `Limite de ${this.maxSelection} arquivos atingido` : '';
  }

  onFileClick(file: IConversationFile): void {
    if (this.isSelected(file)) {
      this.selection = this.selection.filter((id) => id !== file.fileId);
      return;
    }
    if (this.isBlocked(file)) {
      return;
    }
    this.selection = [...this.selection, file.fileId];
  }

  confirm(): void {
    this.filesPicked.emit(this.files.filter((file) => this.isSelected(file)));
    this.close();
  }

  close(): void {
    this.modalRef?.onClose();
  }
}
